import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  inject,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ReportService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {Subject, takeUntil} from 'rxjs';
import {IconPathPipe} from '@app/utils/pipes/icon-path-pipe';
import {saveBlob} from '@app/utils/_msf';
import {Loading} from '@components/ui/loading/loading';

@Component({
  selector: 'app-report-download-action',
  standalone: true,
  imports: [CommonModule, TranslateModule, IconPathPipe, Loading],
  templateUrl: './report-download-action.component.html',
  styleUrl: './report-download-action.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportDownloadActionComponent implements OnDestroy {
  private readonly reportService = inject(ReportService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private readonly activeReaders: FileReader[] = [];

  @Input() reportId: string | null = null;

  loading = false;

  ngOnDestroy(): void {
    this.activeReaders.forEach(r => {
      try {
        r.abort();
      } catch {
        /* ignore */
      }
    });
    this.activeReaders.length = 0;
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDownloadClick(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.reportId || this.loading) {
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.reportService
      .cropMonitoringReportGetFileIdGet(this.reportId, this.translate.currentLang || 'ru-RU', 'body', false, {
        httpHeaderAccept: 'text/html',
      } as any)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: unknown) => {
          this.loading = false;
          const blob =
            data instanceof Blob ? data : new Blob([data as BlobPart], {type: 'text/html;charset=utf-8'});
          saveBlob(blob, `report-${this.reportId}.html`);

          const reader = new FileReader();
          this.activeReaders.push(reader);
          reader.onload = () => {
            const idx = this.activeReaders.indexOf(reader);
            if (idx > -1) {
              this.activeReaders.splice(idx, 1);
            }
            if (this.destroy$.closed) {
              return;
            }
            const htmlContent = reader.result as string;
            const newWindow = window.open('', '_blank');
            if (newWindow) {
              newWindow.document.write(htmlContent);
              newWindow.document.close();
            }
          };
          reader.onerror = reader.onabort = () => {
            const idx = this.activeReaders.indexOf(reader);
            if (idx > -1) {
              this.activeReaders.splice(idx, 1);
            }
          };
          reader.readAsText(blob);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }
}
